
import React, { useState, useEffect } from 'react';
import { 
  FileText, TrendingUp, Sparkles, Download, 
  ChevronRight, Save, Trash2, Edit3, 
  CheckCircle2, AlertTriangle, Target,
  X, Printer, FileSpreadsheet, Loader2, Bot,
  Monitor
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, Cell 
} from 'recharts';
import * as db from '../lib/supabase-service';
import reportingService from '../lib/reporting-service';

interface IAReportGeneratorProps {
  user: any;
  onClose: () => void;
}

const cn = (...classes: any[]) => classes.filter(Boolean).join(' ');

const MONTHS_FULL = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];
const MONTHS_SHORT = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

const IAReportGenerator: React.FC<IAReportGeneratorProps> = ({ user, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [hierarchy, setHierarchy] = useState<any>(null);
  const [selectedInstance, setSelectedInstance] = useState<any>(null);
  const [selectedMonth, setSelectedMonth] = useState(MONTHS_SHORT[new Date().getMonth()]);
  const [selectedYear] = useState(2026);
  const [reportData, setReportData] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);

  // States for AI Narrative
  const [execSummary, setExecSummary] = useState("");
  const [observations, setObservations] = useState("");
  const [recommendations, setRecommendations] = useState("");

  useEffect(() => {
    db.getHierarchy(user).then(h => {
      setHierarchy(h);
      setLoading(false);
    });
  }, [user]);

  const handleGenerateReport = async (instance: any) => {
    try {
      setReportLoading(true);
      setSelectedInstance(instance);
      
      const monthNum = MONTHS_SHORT.indexOf(selectedMonth) + 1;
      const data = await reportingService.fetchBoardReportData(instance.id, monthNum, selectedYear);
      
      setReportData(data);
      
      // Initialize narrative states
      setExecSummary("Solicitando análisis a la IA...");
      setObservations("");
      setRecommendations("");

      const narrative = await reportingService.generateReportNarrative(
        instance.name,
        MONTHS_FULL[monthNum-1],
        data.indicators,
        data.performance_total
      );

      setExecSummary(narrative.summary);
      setObservations(narrative.observations);
      setRecommendations(narrative.recommendations);

    } catch (err: any) {
      alert("Error al generar reporte: " + err.message);
    } finally {
      setReportLoading(false);
    }
  };

  const handleExportPDF = () => {
    // @ts-ignore
    if (typeof html2pdf === 'undefined') {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
      script.onload = () => handleExportPDF();
      document.head.appendChild(script);
      return;
    }
    
    const element = document.getElementById('strategic-report-content');
    const opt = {
      margin: 0.5,
      filename: `INFORME_ESTRATEGICO_${selectedInstance?.name || 'CPX'}_${selectedMonth}_2026.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    // @ts-ignore
    html2pdf().set(opt).from(element).save();
  };

  const handleExportExcel = () => {
    if (!reportData) return;
    
    // Simple CSV export as fallback
    let csv = "Indicador,Peso,Meta Mes,Real Mes,Cumplimiento,Estatus\n";
    reportData.indicators.forEach((ind: any) => {
      csv += `"${ind.nombre}",${ind.peso},${ind.meta_mes},${ind.real_mes},${ind.cumplimiento}%,${ind.status}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `KPIS_${selectedInstance?.name}_${selectedMonth}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return (
    <div className="p-12 text-center text-emerald-500 font-black animate-pulse uppercase tracking-[0.5em]">
      Analizando Estructura...
    </div>
  );

  const allInstances = hierarchy 
    ? [...(hierarchy.commissions || []), ...(hierarchy.independents || [])]
    : [];

  return (
    <div className="h-full flex flex-col bg-[#0a0f1e] text-white">
      {/* HEADER INTEGRADO */}
      <div className="flex-shrink-0 flex items-center justify-between px-8 py-6 border-b border-white/10 glass-plus">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-emerald-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
            <FileText className="text-black w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-black uppercase tracking-tighter">Generador <span className="text-emerald-400">Estratégico IA</span></h2>
            <p className="text-[10px] text-white/40 font-bold uppercase tracking-widest">v2.0 Beta — Automatización Corporativa</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <select 
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-xs font-black uppercase tracking-widest focus:outline-none focus:border-emerald-500/50"
          >
            {MONTHS_SHORT.map((m, i) => (
              <option key={m} value={m} className="bg-[#0a0f1e] text-white">{MONTHS_FULL[i]} 2026</option>
            ))}
          </select>
          <button 
            onClick={onClose}
            className="p-3 hover:bg-white/5 rounded-xl transition-all opacity-50 hover:opacity-100"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
        {!selectedInstance ? (
          <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4">
            <div className="text-center space-y-4">
              <Bot className="w-16 h-16 text-emerald-500 mx-auto animate-bounce" />
              <h3 className="text-2xl font-black uppercase tracking-tight">Selecciona el Tablero a Reportar</h3>
              <p className="text-white/50 text-sm max-w-lg mx-auto">
                La IA analizará automáticamente las varianzas, cumplimiento y tendencias de los indicadores internos para generar un informe listo para firma.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {allInstances.map((inst: any) => (
                <button
                  key={inst.id}
                  onClick={() => handleGenerateReport(inst)}
                  className="p-6 rounded-3xl bg-white/5 border border-white/10 hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-all flex items-center justify-between group"
                >
                  <div className="text-left">
                    <p className="text-xs font-black text-emerald-500 mb-1 uppercase tracking-widest">{inst.type || 'COMISIÓN'}</p>
                    <p className="text-lg font-black uppercase leading-tight">{inst.name}</p>
                    <p className="text-[10px] text-white/30 uppercase font-bold mt-1 tracking-widest">{inst.president_name || 'Sin titular'}</p>
                  </div>
                  <ChevronRight className="w-6 h-6 text-white/10 group-hover:text-emerald-500 transition-colors" />
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-5xl mx-auto space-y-12 pb-20">
            {reportLoading ? (
              <div className="py-32 text-center space-y-6">
                <Loader2 className="w-16 h-16 text-emerald-500 mx-auto animate-spin" />
                <p className="text-emerald-400 font-black uppercase tracking-[0.3em] animate-pulse">Analizando Bienestar Social y Desempeño...</p>
              </div>
            ) : (
              <motion.div 
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-8"
              >
                {/* TOOLBAR */}
                <div className="flex flex-wrap items-center justify-between gap-4 sticky top-0 bg-[#0a0f1e]/80 backdrop-blur-xl p-4 rounded-3xl border border-white/10 z-30 shadow-2xl">
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setSelectedInstance(null)}
                      className="p-2 hover:bg-white/10 rounded-lg text-white/50 hover:text-white transition-all"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                    <div className="h-6 w-px bg-white/10" />
                    <p className="text-xs font-black uppercase">{selectedInstance.name}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setIsEditing(!isEditing)}
                      className={cn(
                        "px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                        isEditing ? "bg-emerald-600 text-white" : "bg-white/5 text-white/70 hover:bg-white/10"
                      )}
                    >
                      {isEditing ? 'Finalizar Edición' : 'Editar Narrativa'}
                    </button>
                    <button 
                      onClick={handleExportExcel}
                      className="p-3 bg-white/5 hover:bg-emerald-500/10 rounded-xl transition-all text-emerald-400 border border-emerald-500/20"
                    >
                      <FileSpreadsheet className="w-5 h-5" />
                    </button>
                    <button 
                      onClick={handleExportPDF}
                      className="px-6 py-2 bg-emerald-500 text-black rounded-xl text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-all flex items-center gap-2"
                    >
                      <Printer size={16} /> Descargar Informe
                    </button>
                  </div>
                </div>

                {/* REPORT CONTENT AREA */}
                <div 
                  id="strategic-report-content" 
                  className="bg-white p-12 md:p-16 rounded-[4rem] text-slate-900 shadow-2xl overflow-hidden relative"
                >
                  <div className="absolute top-0 right-0 p-12 opacity-10 pointer-events-none">
                    <img src="/assets/logo-coparmex.jpg" className="w-48 h-auto" alt="" />
                  </div>

                  {/* COVER */}
                  <div className="border-b-[4px] border-emerald-600 pb-12 mb-12">
                    <div className="flex flex-col gap-2 mb-8">
                       <span className="w-fit bg-emerald-600 text-white text-[9px] font-black px-4 py-1.5 rounded-full uppercase tracking-[0.2em]">Exclusivo Miembros Nacionales</span>
                       <h1 className="text-5xl font-black uppercase tracking-tighter leading-[0.9] italic">Informe Mensual de <br />Desempeño Estratégico</h1>
                    </div>
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-2">Instancia Reportada</p>
                        <h2 className="text-2xl font-black text-emerald-700 uppercase">{selectedInstance.name}</h2>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-2">Periodo</p>
                        <h2 className="text-2xl font-black text-emerald-600 uppercase italic">{selectedMonth} 2026</h2>
                      </div>
                    </div>
                  </div>

                  {/* EXECUTIVE SUMMARY */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 mb-16">
                    <div className="lg:col-span-2 space-y-12">
                      <section>
                         <h3 className="text-[11px] font-black uppercase text-emerald-600 tracking-[0.2em] mb-4 flex items-center gap-2">
                           <Sparkles size={14} /> Resumen Ejecutivo AI
                         </h3>
                         {isEditing ? (
                           <textarea 
                             value={execSummary}
                             onChange={(e) => setExecSummary(e.target.value)}
                             className="w-full h-48 bg-emerald-50/50 border border-emerald-100 p-6 rounded-3xl text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                           />
                         ) : (
                           <p className="text-lg text-slate-700 leading-relaxed font-medium">
                              {execSummary}
                           </p>
                         )}
                      </section>

                      <section>
                         <h3 className="text-[11px] font-black uppercase text-emerald-600 tracking-[0.2em] mb-4 flex items-center gap-2">
                           <Bot size={14} /> Análisis y Observaciones
                         </h3>
                         {isEditing ? (
                           <textarea 
                             value={observations}
                             onChange={(e) => setObservations(e.target.value)}
                             className="w-full h-48 bg-indigo-50/30 border border-indigo-100 p-6 rounded-3xl text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                           />
                         ) : (
                           <div className="bg-slate-50 p-8 rounded-[3rem] border border-slate-100 whitespace-pre-wrap text-sm text-slate-600 leading-relaxed">
                              {observations}
                           </div>
                         )}
                      </section>
                    </div>

                    <div className="space-y-10">
                       <div className="bg-emerald-600 text-white p-10 rounded-[3rem] shadow-xl relative overflow-hidden">
                          <div className="absolute top-[-20px] right-[-20px] w-40 h-40 bg-white/10 rounded-full blur-3xl" />
                          <p className="text-[10px] font-black uppercase tracking-widest opacity-60 mb-2">Cumplimiento Global</p>
                          <h4 className="text-6xl font-black italic mb-2">{reportData.performance_total}%</h4>
                          <div className="h-2 w-full bg-black/20 rounded-full overflow-hidden">
                            <motion.div 
                              initial={{ width: 0 }}
                              animate={{ width: `${reportData.performance_total}%` }}
                              transition={{ duration: 1.5, ease: "easeOut" }}
                              className="h-full bg-white shadow-[0_0_15px_white]"
                            />
                          </div>
                          <p className="text-[9px] font-bold uppercase mt-4 tracking-wider">Índice de Gobernanza Articulada</p>
                       </div>

                       <section>
                          <h3 className="text-[11px] font-black uppercase text-slate-900 tracking-[0.2em] mb-4">Recomendaciones</h3>
                          <div className="space-y-4">
                             {recommendations.split('\n').filter(r => r.trim()).map((rec, i) => (
                               <div key={i} className="flex gap-4 p-4 bg-amber-50 rounded-2xl border border-amber-100">
                                  <AlertTriangle className="text-amber-600 w-5 h-5 flex-shrink-0" />
                                  <p className="text-xs font-bold text-amber-800 leading-tight">{rec.replace(/^[0-9.-]*/, "").trim()}</p>
                               </div>
                             ))}
                          </div>
                       </section>

                       <section>
                          <h3 className="text-[11px] font-black uppercase text-slate-900 tracking-[0.2em] mb-4">Firma Digital</h3>
                          <div className="p-8 border-2 border-dashed border-slate-200 rounded-[2rem] text-center space-y-4">
                             <div className="h-16 flex items-center justify-center">
                                <span className="font-['Courier_New'] italic text-slate-400">Firmado digitalmente por IA</span>
                             </div>
                             <div className="h-px bg-slate-200" />
                             <div>
                                <p className="text-xs font-black uppercase leading-none">{selectedInstance.president_name || 'Titular Na'}</p>
                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">Titular Responsable</p>
                             </div>
                          </div>
                       </section>
                    </div>
                  </div>

                  {/* CHARTS & TABLES */}
                  <div className="space-y-12">
                    <section>
                       <h3 className="text-sm font-black uppercase text-slate-900 flex items-center gap-3 mb-8">
                         <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center text-indigo-600">
                           <TrendingUp size={18} />
                         </div>
                         Tendencia de Cumplimiento (Histórico Reciente)
                       </h3>
                       <div className="h-72 w-full">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={reportData.chart_data}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                              <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 900 }} />
                              <YAxis domain={[0, 'auto']} axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 900 }} />
                              <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '20px', border: 'none', boxShadow: '0 20px 50px rgba(0,0,0,0.1)' }} />
                              <Legend iconType="circle" wrapperStyle={{ paddingTop: 20, fontSize: 10, fontWeight: 900, textTransform: 'uppercase' }} />
                              <Bar dataKey="meta" name="Meta" fill="#e2e8f0" radius={[6, 6, 0, 0]} barSize={40} />
                              <Bar dataKey="real" name="Avance Real" fill="#10b981" radius={[6, 6, 0, 0]} barSize={40} />
                            </BarChart>
                          </ResponsiveContainer>
                       </div>
                    </section>

                    <section>
                       <h3 className="text-sm font-black uppercase text-slate-900 flex items-center gap-3 mb-8">
                         <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600">
                           <Target size={18} />
                         </div>
                         Tabla de Desempeño Consolidada
                       </h3>
                       <div className="border border-slate-100 rounded-[2.5rem] overflow-hidden">
                          <table className="w-full text-left">
                            <thead>
                              <tr className="bg-slate-900 text-white text-[9px] uppercase font-black tracking-widest">
                                <th className="p-6">Indicador</th>
                                <th className="p-6 text-center">Peso</th>
                                <th className="p-6 text-right">Meta Mes</th>
                                <th className="p-6 text-right">Real Mes</th>
                                <th className="p-6 text-right">Cumplimiento</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reportData.indicators.map((ind: any) => (
                                <tr key={ind.id} className="border-b border-slate-50 last:border-none">
                                  <td className="p-6">
                                    <p className="text-xs font-black uppercase text-slate-800">{ind.nombre}</p>
                                    <p className="text-[9px] text-slate-400 font-bold uppercase">{ind.unidad}</p>
                                  </td>
                                  <td className="p-6 text-center">
                                    <span className="text-[10px] font-black bg-slate-100 px-2 py-1 rounded text-slate-600">{ind.peso}</span>
                                  </td>
                                  <td className="p-6 text-right text-sm font-bold text-slate-500">{ind.meta_mes}</td>
                                  <td className="p-6 text-right text-sm font-black text-slate-900">{ind.real_mes}</td>
                                  <td className="p-6 text-right">
                                    <div className="flex flex-col items-end">
                                      <span className={cn(
                                        "text-sm font-black italic",
                                        ind.status === 'SATISFACTORIO' ? 'text-emerald-600' :
                                        ind.status === 'PRECAUCIÓN' ? 'text-amber-600' : 'text-red-600'
                                      )}>
                                        {ind.cumplimiento}%
                                      </span>
                                      <span className="text-[8px] font-black uppercase tracking-tighter opacity-40">{ind.status}</span>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                       </div>
                    </section>
                  </div>
                  
                  {/* FOOTER REPORT */}
                  <div className="mt-20 pt-10 border-t border-slate-100 flex justify-between items-center text-[9px] font-black text-slate-300 uppercase tracking-[0.4em]">
                     <span>Desarrollado por IAPRIORI</span>
                     <span>© 2026 COPARMEX Nacional</span>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        )}
      </div>

      <style>{`
        .glass-plus {
          background: rgba(10, 15, 30, 0.7);
          backdrop-filter: blur(20px);
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 20px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.1);
        }
        @media print {
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default IAReportGenerator;
